<script>
import Layout from '@/layouts/main.vue';
import PageHeader from '@/components/page-header';
import { api, confirmar, erro, sucesso } from '@/helpers/common/api';
import CampoPersonalizadoForm from '@/components/crm/contato/CampoPersonalizadoForm.vue';

export default {
  name: 'ContatoCamposPersonalizadosView',
  components: { Layout, PageHeader, CampoPersonalizadoForm },
  data() {
    return {
      loading: false,
      campos: [],
      modalAberto: false,
      campoEditando: null,
    };
  },
  async created() {
    await this.carregarCampos();
  },
  methods: {
    async carregarCampos() {
      this.loading = true;
      try {
        this.campos = await api.read('/campopersonalizado?incluirOpcoesInativas=true') || [];
      } finally {
        this.loading = false;
      }
    },
    abrirModal(campo = null) {
      this.campoEditando = campo;
      this.modalAberto = true;
    },
    fecharModal() {
      this.modalAberto = false;
      this.campoEditando = null;
    },
    async salvarCampo(payload) {
      const rota = this.campoEditando ? `/campopersonalizado/${this.campoEditando.id}` : '/campopersonalizado';
      const response = this.campoEditando ? await api.update(rota, payload) : await api.create(rota, payload);

      if (!response) {
        erro({ title: 'Erro ao salvar campo personalizado.' });
        return;
      }

      sucesso({ title: 'Campo personalizado salvo com sucesso!' });
      this.fecharModal();
      await this.carregarCampos();
    },
    inativarCampo(campo) {
      confirmar({
        options: {
          title: 'Inativar campo personalizado?',
          text: `O campo "${campo.nome_exibicao}" deixará de aparecer em novos cadastros.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sim, inativar',
          cancelButtonText: 'Cancelar',
          customClass: {
            confirmButton: 'btn btn-danger w-xs me-2',
            cancelButton: 'btn btn-light w-xs',
          },
          buttonsStyling: false,
        },
        confirmado: async () => {
          const response = await api.destroy(`/campopersonalizado/${campo.id}`);
          if (response === false) {
            erro({ title: 'Erro ao inativar campo personalizado.' });
            return;
          }
          sucesso({ title: 'Campo personalizado inativado.' });
          await this.carregarCampos();
        },
      });
    },
    resumoOpcoes(campo) {
      const opcoes = Array.isArray(campo.opcoes) ? campo.opcoes.filter((opcao) => opcao.ativo !== false) : [];
      return opcoes.map((opcao) => opcao.label).join(', ') || 'Sem opções';
    },
    badgeTipo(tipo) {
      const map = {
        text: 'bg-primary-subtle text-primary',
        number: 'bg-info-subtle text-info',
        dropdown: 'bg-warning-subtle text-warning',
        multiselect_single: 'bg-warning-subtle text-warning',
        multiselect_tags: 'bg-warning-subtle text-warning',
        radio: 'bg-warning-subtle text-warning',
        checkbox: 'bg-success-subtle text-success',
        date: 'bg-secondary-subtle text-secondary',
        time: 'bg-secondary-subtle text-secondary',
        color: 'bg-danger-subtle text-danger',
      };
      return map[tipo] || 'bg-light text-dark';
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader title="Campos Personalizados" pageTitle="Cadastros" />

    <BCard no-body>
      <div class="card-header d-flex justify-content-between align-items-center">
        <div>
          <h5 class="card-title mb-1">Catálogo de campos adicionais</h5>
          <p class="text-muted mb-0">Gerencie os campos exibidos no cadastro de contatos e na grade.</p>
        </div>
        <BButton variant="success" @click="abrirModal()">
          <i class="ri-add-line me-1"></i> Novo campo
        </BButton>
      </div>

      <div class="card-body">
        <div v-if="loading" class="text-center py-5">
          <BSpinner variant="primary" />
        </div>

        <div v-else class="table-responsive">
          <table class="table table-borderless align-middle table-nowrap mb-0">
            <thead class="table-light">
              <tr>
                <th>Campo</th>
                <th>Chave</th>
                <th>Tipo</th>
                <th>Opções</th>
                <th>Configuração</th>
                <th class="text-end">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="campo in campos" :key="campo.id">
                <td>
                  <div class="fw-semibold">{{ campo.nome_exibicao }}</div>
                  <small class="text-muted">Ordem {{ campo.ordem_exibicao }}</small>
                </td>
                <td><code>{{ campo.chave_tecnica }}</code></td>
                <td>
                  <span class="badge" :class="badgeTipo(campo.tipo_campo)">{{ campo.tipo_campo }}</span>
                </td>
                <td class="text-muted small">{{ resumoOpcoes(campo) }}</td>
                <td>
                  <span class="badge me-1" :class="campo.ativo ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'">
                    {{ campo.ativo ? 'Ativo' : 'Inativo' }}
                  </span>
                  <span class="badge" :class="campo.obrigatorio ? 'bg-info-subtle text-info' : 'bg-light text-muted'">
                    {{ campo.obrigatorio ? 'Obrigatório' : 'Opcional' }}
                  </span>
                </td>
                <td class="text-end">
                  <BButton variant="soft-info" size="sm" class="me-2" @click="abrirModal(campo)">
                    <i class="ri-edit-line"></i>
                  </BButton>
                  <BButton v-if="campo.ativo" variant="soft-danger" size="sm" @click="inativarCampo(campo)">
                    <i class="ri-forbid-line"></i>
                  </BButton>
                </td>
              </tr>
              <tr v-if="!campos.length">
                <td colspan="6" class="text-center text-muted py-4">Nenhum campo personalizado cadastrado.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </BCard>

    <CampoPersonalizadoForm
      :visivel="modalAberto"
      :campo="campoEditando"
      @fechar="fecharModal"
      @salvar="salvarCampo"
    />
  </Layout>
</template>